/**
 * Unified Task List Component
 * 
 * List view for tasks using the unified task management system.
 * Shares the same logic and components as the Kanban view.
 */

import React, { useState, useCallback, useMemo, useEffect } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  FormControlLabel,
  Switch,
  useTheme,
  Paper,
  Divider,
  Collapse,
  IconButton,
  Button,
  Stack
} from '@mui/material';
import {
  List as ListIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Add as AddIcon
} from '@mui/icons-material';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { useSnackbar } from 'notistack';

// Core components
import FilterBar from '../core/FilterBar';
import ActiveFilters from '../core/ActiveFilters';
import FilterMenu from '../core/FilterMenu';
import { SortMenu } from '../../design-system';

// Unified components
import UnifiedTaskCard from './UnifiedTaskCard';
import EmptyTaskState from './EmptyTaskState';

// Hooks and utilities
import { useUnifiedTaskOperations } from '../../hooks/useUnifiedTaskOperations';
import useConfirmDialog from '../../hooks/useConfirmationDialog';
import useProjectPermissions from '../../hooks/useProjectPermissions';
import { useSectionOperations } from '../../hooks/useSectionOperations';
import { useProjectRealtimeOptimized } from '../../hooks/useProjectRealtimeOptimized';
// import { useBackgroundSync } from '../../hooks/useBackgroundSync';
import useTaskStore from '../../stores/taskStore';
import useProjectStore from '../../stores/projectStore';
import useDebounce from '../../hooks/useDebounce';
import useCustomTaskOrder from '../../hooks/useCustomTaskOrder';
import { parseSectionId } from '../../utils/dragDropUtils';

const UnifiedTaskList = ({ 
  projectId,
  sections: propSections = [],
  teamMembers = [],
  project = null,
  showProject = false, // For "My Tasks" view
  groupBySections = true, // Whether to group by sections or show flat list
  viewMode = null // 'all' or 'my' for different task views
}) => {
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();
  
  // ========================================
  // PERMISSIONS
  // ========================================
  
  const {
    canCreateTask,
    canUpdateTask,
    canDeleteTask
  } = useProjectPermissions(projectId);

  // ========================================
  // STATE MANAGEMENT
  // ========================================

  const [search, setSearch] = useState(''); // Keep empty since search is hidden
  const [showCompletedTasks, setShowCompletedTasks] = useState(true);
  const [expandedSections, setExpandedSections] = useState({});
  const [filters, setFilters] = useState({});
  const [sort, setSort] = useState({ field: 'orderIndex', direction: 'asc' });
  const [isDragging, setIsDragging] = useState(false);
  
  // Filter/sort menu state
  const [filterMenuAnchorEl, setFilterMenuAnchorEl] = useState(null);
  const [sortMenuAnchorEl, setSortMenuAnchorEl] = useState(null);

  const debouncedSearch = useDebounce(search, 300);

  // ========================================
  // UNIFIED TASK OPERATIONS
  // ========================================

  const {
    tasks,
    groupedTasks,
    loading,
    error,
    operationInProgress,
    sections,
    createTask,
    updateTask,
    updateTaskStatus,
    assignTask,
    updateTaskPriority,
    updateTaskDueDate,
    deleteTask,
    refreshTasks,
    getTasksForSection,
    handleLoadMore,
    getRemainingTasks,
    getSectionPagination,
    moveTask,
    reorderTasks
  } = useUnifiedTaskOperations(projectId, {
    viewType: groupBySections ? 'kanban' : 'list', // Use kanban logic when grouping by sections
    viewMode: viewMode
  });

  // ========================================
  // CUSTOM TASK ORDERING (for All/My tasks views)
  // ========================================
  
  const { getOrderedTasks, updateTaskOrder, resetOrder, hasCustomOrder } = useCustomTaskOrder(viewMode, tasks);
  
  // Apply custom ordering only for all/my tasks views
  const orderedTasks = useMemo(() => {
    if (viewMode === 'all' || viewMode === 'my') {
      return getOrderedTasks(tasks);
    }
    return tasks;
  }, [tasks, viewMode, getOrderedTasks]);

  // ========================================
  // SECTION OPERATIONS
  // ========================================

  const {
    createSection,
    updateSection,
    deleteSection,
    reorderSections,
    loadSections
  } = useSectionOperations(projectId);

  // Get sections from store
  const { projectSections } = useTaskStore();
  const sectionsFromStore = projectSections[projectId] || [];
  
  // Get projects from store for filtering
  const { projects, fetchProjects } = useProjectStore();

  // Load sections on mount
  useEffect(() => {
    if (projectId) {
      loadSections();
    }
  }, [projectId]); // Removed loadSections from deps to prevent loops

  // Initialize expanded sections when sections change
  useEffect(() => {
    if (groupBySections && sections.length > 0) {
      setExpandedSections(prev => {
        const newExpanded = { ...prev };
        // Initialize all sections as expanded if not already set
        sections.forEach(section => {
          if (newExpanded[section._id] === undefined) {
            newExpanded[section._id] = true;
          }
        });
        // Also initialize the default "Draft" as expanded
        if (newExpanded['no-section'] === undefined) {
          newExpanded['no-section'] = true;
        }
        return newExpanded;
      });
    }
  }, [sections, groupBySections]);
  
  // Load projects for filter if in all/my tasks view
  useEffect(() => {
    if ((viewMode === 'all' || viewMode === 'my') && !projects.length) {
      fetchProjects();
    }
  }, [viewMode, projects.length, fetchProjects]);

  // ========================================
  // REAL-TIME UPDATES
  // ========================================

  // Enable real-time updates for project views only
  useProjectRealtimeOptimized(projectId, refreshTasks, loadSections);
  
  // ========================================
  // BACKGROUND SYNC - DISABLED
  // ========================================
  
  // Background sync disabled - only real-time updates via sockets

  // ========================================
  // CONFIRMATION DIALOG
  // ========================================

  const [ConfirmDialog, confirm] = useConfirmDialog();

  // ========================================
  // TASK OPERATIONS
  // ========================================

  const handleCreateTask = useCallback((taskData) => {
    const { openTaskDrawer } = useTaskStore.getState();
    
    if (openTaskDrawer) {
      // When creating tasks in project list view, hide project/section fields
      openTaskDrawer({
        ...taskData,
        projectId
      }, {
        hideProjectField: true,
        hideSectionField: true
      });
    } else {
      // Fallback to direct creation
      createTask({
        title: 'New Task',
        projectId,
        ...taskData
      });
    }
  }, [projectId, createTask]);

  const handleEditTask = useCallback((task) => {
    const { openTaskDrawer } = useTaskStore.getState();
    
    if (openTaskDrawer) {
      // When updating tasks in project list view, hide project/section fields
      openTaskDrawer(task, {
        hideProjectField: true,
        hideSectionField: true
      });
    }
  }, []);

  const handleDeleteTask = useCallback(async (taskId) => {
    const confirmed = await confirm({
      title: 'Delete Task',
      message: 'Are you sure you want to delete this task?',
      confirmText: 'Delete'
    });

    if (confirmed) {
      await deleteTask(taskId);
    }
  }, [deleteTask, confirm]);

  const handleTaskClick = useCallback((task) => {
    handleEditTask(task);
  }, [handleEditTask]);

  // ========================================
  // SECTION MANAGEMENT
  // ========================================

  const handleToggleSectionExpand = useCallback((sectionId) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId]
    }));
  }, []);

  // ========================================
  // DRAG AND DROP
  // ========================================

  const handleDragStart = useCallback(() => {
    setIsDragging(true);
  }, []);

  const handleDragEnd = useCallback((result) => {
    console.log('List view drag ended:', result);
    setIsDragging(false);
    
    if (!result.destination) {
      console.log('No destination - dropped outside');
      return;
    }
    
    const { destination, source, draggableId } = result;
    
    // Same position
    if (
      destination.droppableId === source.droppableId &&
      destination.index === source.index
    ) {
      console.log('Same position - no change');
      return;
    }
    
    console.log('Processing drag:', {
      source: source.droppableId,
      destination: destination.droppableId,
      draggableId,
      groupBySections
    });
    
    // Handle task reordering within the same section
    if (groupBySections && destination.droppableId === source.droppableId) {
      const sectionId = parseSectionId(source.droppableId);
      const sectionTasks = getTasksForSection(sectionId);
      
      console.log('Reordering within section:', sectionId, 'tasks:', sectionTasks?.length);
      
      if (!sectionTasks || sectionTasks.length === 0) {
        console.error('No tasks found in section:', sectionId);
        return;
      }
      
      const newTaskIds = Array.from(sectionTasks.map(t => t._id));
      newTaskIds.splice(source.index, 1);
      newTaskIds.splice(destination.index, 0, draggableId);
      
      console.log('New task order:', newTaskIds);
      reorderTasks(sectionId, newTaskIds).catch(error => {
        console.error('Error reordering tasks:', error);
        enqueueSnackbar('Failed to reorder tasks', { variant: 'error' });
      });
    } else if (groupBySections) {
      // Moving between sections
      const destSectionId = parseSectionId(destination.droppableId);
      console.log('Moving task between sections to:', destSectionId);
      
      moveTask(draggableId, destSectionId, destination.index).catch(error => {
        console.error('Error moving task:', error);
        enqueueSnackbar('Failed to move task', { variant: 'error' });
      });
    } else {
      // Flat list reordering
      console.log('Flat list reordering');
      const flatTasks = orderedTasks.filter(task => {
        // Apply same filters as in getFilteredTasksForSection
        if (!showCompletedTasks && task.status === 'done') return false;
        if (debouncedSearch && typeof debouncedSearch === 'string') {
          const searchLower = debouncedSearch.toLowerCase();
          if (!task.title.toLowerCase().includes(searchLower) && 
              !task.description?.toLowerCase().includes(searchLower)) {
            return false;
          }
        }
        return true;
      });
      
      const newTaskIds = Array.from(flatTasks.map(t => t._id));
      newTaskIds.splice(source.index, 1);
      newTaskIds.splice(destination.index, 0, draggableId);
      
      console.log('Flat list new order:', newTaskIds);
      
      // For all/my tasks views, use client-side ordering
      if (viewMode === 'all' || viewMode === 'my') {
        updateTaskOrder(newTaskIds);
        enqueueSnackbar('Task order updated', { variant: 'success' });
      } else {
        // For project views, use server-side ordering
        reorderTasks(null, newTaskIds).catch(error => {
          console.error('Error reordering tasks:', error);
          enqueueSnackbar('Failed to reorder tasks', { variant: 'error' });
        });
      }
    }
  }, [groupBySections, getTasksForSection, reorderTasks, moveTask, enqueueSnackbar, orderedTasks, showCompletedTasks, debouncedSearch, viewMode, updateTaskOrder]);

  // ========================================
  // FILTERING AND SORTING
  // ========================================

  const filterConfig = useMemo(() => {
    const baseFilters = [
      {
        name: 'status',
        label: 'Status',
        options: [
          { value: 'to_do', label: 'To Do' },
          { value: 'in_progress', label: 'In Progress' },
          { value: 'in_review', label: 'In Review' },
          { value: 'done', label: 'Done' },
          { value: 'cancelled', label: 'Cancelled' }
        ]
      },
      {
        name: 'priority',
        label: 'Priority',
        options: [
          { value: 'urgent', label: 'Urgent' },
          { value: 'high', label: 'High' },
          { value: 'medium', label: 'Medium' },
          { value: 'low', label: 'Low' }
        ]
      }
    ];
    
    // For all/my tasks views, add project filter instead of assignee
    if (viewMode === 'all' || viewMode === 'my') {
      baseFilters.push({
        name: 'projectId',
        label: 'Project',
        options: projects.map(project => ({
          value: project._id,
          label: project.name
        }))
      });
    } else {
      // For project-specific views, add both assignee and collaborator filters
      baseFilters.push({
        name: 'assignedTo',
        label: 'Assignee',
        options: (teamMembers || []).filter(member => member && member._id).map(member => ({
          value: member._id,
          label: `${member.firstName || ''} ${member.lastName || ''}`.trim() || 'Unknown User'
        }))
      });
      
      baseFilters.push({
        name: 'collaborators',
        label: 'Collaborators',
        options: (teamMembers || []).filter(member => member && member._id).map(member => ({
          value: member._id,
          label: `${member.firstName || ''} ${member.lastName || ''}`.trim() || 'Unknown User'
        }))
      });
    }
    
    return baseFilters;
  }, [viewMode, projects, teamMembers]);

  const sortOptions = [
    { value: 'orderIndex', label: 'Manual Order' },
    { value: 'title', label: 'Title' },
    { value: 'priority', label: 'Priority' },
    { value: 'dueDate', label: 'Due Date' },
    { value: 'createdAt', label: 'Created Date' },
    { value: 'updatedAt', label: 'Updated Date' },
    { value: 'status', label: 'Status' }
  ];

  // Filter/sort handlers
  const handleOpenFilterMenu = useCallback((event) => {
    setFilterMenuAnchorEl(event.currentTarget);
  }, []);

  const handleCloseFilterMenu = useCallback(() => {
    setFilterMenuAnchorEl(null);
  }, []);

  const handleOpenSortMenu = useCallback((event) => {
    setSortMenuAnchorEl(event.currentTarget);
  }, []);

  const handleCloseSortMenu = useCallback(() => {
    setSortMenuAnchorEl(null);
  }, []);

  const handleFilterChange = useCallback((filterKey, value) => {
    setFilters(prev => ({
      ...prev,
      [filterKey]: value
    }));
  }, []);

  const handleSortChange = useCallback((field) => {
    setSort(prev => ({ ...prev, field }));
  }, []);

  const handleSortDirectionChange = useCallback((direction) => {
    setSort(prev => ({ ...prev, direction }));
  }, []);

  const handleClearFilter = useCallback((filterKey) => {
    setFilters(prev => {
      const newFilters = { ...prev };
      delete newFilters[filterKey];
      return newFilters;
    });
  }, []);

  const handleClearAllFilters = useCallback(() => {
    setFilters({});
    // Don't clear search since it's hidden
    setShowCompletedTasks(true);
  }, []);

  // Apply filters to tasks
  const getFilteredTasks = useCallback((taskList) => {
    if (!Array.isArray(taskList)) {
      return [];
    }
    let filteredTasks = [...taskList];

    // Apply search filter
    if (debouncedSearch && typeof debouncedSearch === 'string') {
      const searchLower = debouncedSearch.toLowerCase();
      filteredTasks = filteredTasks.filter(task => 
        task.title.toLowerCase().includes(searchLower) ||
        task.description?.toLowerCase().includes(searchLower)
      );
    }

    // Apply status filters
    if (!showCompletedTasks) {
      filteredTasks = filteredTasks.filter(task => task.status !== 'done');
    }

    // Apply other filters
    Object.entries(filters).forEach(([key, value]) => {
      if (value && value.length > 0) {
        filteredTasks = filteredTasks.filter(task => {
          if (key === 'assignedTo') {
            return value.includes(task.assignedTo?._id);
          }
          if (key === 'collaborators') {
            // Filter by collaborators
            return task.collaborators?.some(collab => 
              value.includes(collab.user?._id || collab.user)
            );
          }
          if (key === 'projectId') {
            const taskProjectId = task.projectId?._id || task.projectId;
            return value.includes(taskProjectId);
          }
          return value.includes(task[key]);
        });
      }
    });

    return filteredTasks;
  }, [debouncedSearch, showCompletedTasks, filters]);

  // Check if there are active filters (excluding search since it's hidden)
  const hasActiveFilters = Object.keys(filters).length > 0 || 
    !showCompletedTasks; // Only count as active filter when HIDING completed tasks

  // ========================================
  // COMPUTED DATA
  // ========================================

  // Use sections from unified hook (which uses the original working store)
  const listSections = [
    { _id: 'no-section', name: 'Draft', isDefaultSection: true },
    ...(sections || [])
  ];

  const displayData = useMemo(() => {
    if (groupBySections) {
      return listSections.map(section => {
        const sectionId = section.isDefaultSection ? null : section._id;
        const sectionTasks = getTasksForSection(sectionId) || [];
        const filteredTasks = getFilteredTasks(sectionTasks);
        
        // Get the total count from pagination data (includes all tasks, not just loaded ones)
        const paginationData = getSectionPagination(sectionId);
        const totalCount = paginationData.total || sectionTasks.length;
        
        return {
          ...section,
          tasks: filteredTasks || [],
          taskCount: totalCount, // Use total count instead of filtered count
          filteredCount: (filteredTasks || []).length // Keep filtered count for reference
        };
      }).filter(section => {
        // Always show sections when there are no active filters
        // Only hide empty sections when filters are applied
        if (!hasActiveFilters) return true;
        return (section.tasks || []).length > 0;
      });
    } else {
      // Flat list of all tasks
      const filteredTasks = getFilteredTasks(orderedTasks || []);
      return [{
        _id: 'all-tasks',
        name: 'All Tasks',
        tasks: filteredTasks || [],
        taskCount: tasks.length, // Total count
        filteredCount: (filteredTasks || []).length, // Filtered count
        isFlat: true
      }];
    }
  }, [
    groupBySections,
    listSections,
    getTasksForSection,
    getFilteredTasks,
    tasks,
    orderedTasks,
    hasActiveFilters,
    getSectionPagination
  ]);

  // ========================================
  // RENDER SECTION
  // ========================================

  const renderSection = useCallback((section) => {
    const isExpanded = expandedSections[section._id] !== false; // Default to expanded
    const showHeader = groupBySections && !section.isFlat;

    return (
      <Paper 
        key={section._id}
        sx={{ 
          mb: 2,
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: 2,
          overflow: 'hidden'
        }}
      >
        {/* Section Header */}
        {showHeader && (
          <Box
            sx={{
              p: 2,
              borderBottom: `1px solid ${theme.palette.divider}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              backgroundColor: theme.palette.action.hover,
              cursor: 'pointer'
            }}
            onClick={() => handleToggleSectionExpand(section._id)}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <IconButton size="small">
                {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
              <Typography variant="subtitle1" fontWeight={600}>
                {section.name}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                ({section.taskCount} tasks)
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {canCreateTask && (
                <Button
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleCreateTask({ 
                      sectionId: section.isDefaultSection ? null : section._id 
                    });
                  }}
                  sx={{ fontSize: '0.75rem' }}
                >
                  Add Task
                </Button>
              )}
            </Box>
          </Box>
        )}

        {/* Section Content */}
        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
          <Droppable droppableId={section._id} type="TASK">
            {(provided, snapshot) => (
              <Box 
                ref={provided.innerRef}
                {...provided.droppableProps}
                sx={{ 
                  p: 1,
                  backgroundColor: snapshot.isDraggingOver 
                    ? theme.palette.action.hover 
                    : 'transparent',
                  transition: 'background-color 0.2s ease'
                }}
              >
                {(section.tasks || []).length === 0 ? (
                  <Box
                    sx={{
                      p: 4,
                      textAlign: 'center',
                      color: theme.palette.text.secondary
                    }}
                  >
                    {hasActiveFilters ? 'No tasks match the current filters' : 'No tasks in this section'}
                  </Box>
                ) : (
                  <Stack spacing={1}>
                    {(section.tasks || []).map((task, index) => (
                      <UnifiedTaskCard
                        key={task._id}
                        task={task}
                        index={index}
                        viewType="list"
                        isDragDisabled={false} // Enable drag in list view
                        onTaskClick={handleTaskClick}
                        onStatusChange={updateTaskStatus}
                        onPriorityChange={updateTaskPriority}
                        onAssigneeChange={assignTask}
                        onQuickEdit={handleEditTask}
                        onDelete={handleDeleteTask}
                        showProject={showProject}
                        compact={true}
                      />
                    ))}
                    {provided.placeholder}
                    
                    {/* Load More Button */}
                    {groupBySections && (section.tasks || []).length > 0 && getRemainingTasks(section.isDefaultSection ? null : section._id) > 0 && (
                      <Box sx={{ p: 2, textAlign: 'center' }}>
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => handleLoadMore(section.isDefaultSection ? null : section._id)}
                          disabled={operationInProgress}
                          sx={{ 
                            fontSize: '0.75rem',
                            textTransform: 'none'
                          }}
                        >
                          Load More ({getRemainingTasks(section.isDefaultSection ? null : section._id)} remaining)
                        </Button>
                      </Box>
                    )}
                  </Stack>
                )}
              </Box>
            )}
          </Droppable>
        </Collapse>
      </Paper>
    );
  }, [
    expandedSections,
    groupBySections,
    handleToggleSectionExpand,
    canCreateTask,
    handleCreateTask,
    hasActiveFilters,
    handleTaskClick,
    updateTaskStatus,
    updateTaskPriority,
    assignTask,
    handleEditTask,
    handleDeleteTask,
    showProject,
    operationInProgress,
    getRemainingTasks,
    handleLoadMore,
    theme
  ]);

  // ========================================
  // RENDER
  // ========================================

  return (
    <Box
      sx={{ 
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        width: '100%'
      }}
    >
      {/* Top Toolbar */}
      <Box sx={{ 
        px: 3, 
        py: 2, 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between',
        borderBottom: `1px solid ${theme.palette.divider}`
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <ListIcon sx={{ mr: 1.5 }} />
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {groupBySections ? 'List' : 'My Tasks'}
          </Typography>
        </Box>
        
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {/* Hide search bar - only show filter and sort buttons */}
          <FilterBar
            searchTerm=""
            onSearchChange={() => {}}
            onClearSearch={() => {}}
            onOpenFilterMenu={handleOpenFilterMenu}
            onOpenSortMenu={handleOpenSortMenu}
            hasActiveFilters={hasActiveFilters}
            hideSearch={true}
            sx={{ width: 'auto' }}
          />
          
          <FormControlLabel
            control={
              <Switch 
                checked={showCompletedTasks}
                onChange={(e) => setShowCompletedTasks(e.target.checked)}
                size="small"
              />
            }
            label={<Typography variant="body2">Show Completed</Typography>}
          />

          {/* Reset Order Button - only show for all/my tasks views with custom order */}
          {(viewMode === 'all' || viewMode === 'my') && hasCustomOrder && (
            <Button
              variant="outlined"
              size="small"
              onClick={() => {
                resetOrder();
                enqueueSnackbar('Task order reset to default', { variant: 'info' });
              }}
            >
              Reset Order
            </Button>
          )}

          {canCreateTask && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleCreateTask({})}
              sx={{ ml: 1 }}
            >
              Add Task
            </Button>
          )}
        </Box>
      </Box>
      
      {/* Active Filters Display */}
      <ActiveFilters
        searchTerm="" // No search since it's hidden
        filters={filters}
        onClearSearch={() => {}}
        onClearFilter={handleClearFilter}
        onClearAllFilters={handleClearAllFilters}
        filterConfig={filterConfig}
        teamMembers={teamMembers}
        projects={projects} // Pass projects for filter display
        showCompletedTasks={showCompletedTasks}
        onToggleShowCompleted={setShowCompletedTasks}
      />
      
      {/* Main Content */}
      <Box
        sx={{
          flexGrow: 1,
          overflow: 'auto',
          p: 2
        }}
      >
        {loading && !tasks.length ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
            <CircularProgress />
          </Box>
        ) : displayData.length === 0 || (displayData.length === 1 && displayData[0]._id === 'no-section' && sections.length === 0) ? (
          <EmptyTaskState
            hasActiveFilters={hasActiveFilters}
            projectId={projectId}
            onCreateTask={handleCreateTask}
          />
        ) : (
          <DragDropContext 
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
          >
            <Box>
              {displayData.map(section => renderSection(section))}
            </Box>
          </DragDropContext>
        )}
      </Box>
      
      {/* Filter Menu */}
      <FilterMenu
        filters={filters}
        onFilterChange={handleFilterChange}
        anchorEl={filterMenuAnchorEl}
        open={Boolean(filterMenuAnchorEl)}
        onClose={handleCloseFilterMenu}
        filterConfig={filterConfig}
        onClearAllFilters={handleClearAllFilters}
      />
      
      {/* Sort Menu */}
      <SortMenu
        sort={sort}
        onSortChange={handleSortChange}
        onSortDirectionChange={handleSortDirectionChange}
        anchorEl={sortMenuAnchorEl}
        open={Boolean(sortMenuAnchorEl)}
        onClose={handleCloseSortMenu}
        sortOptions={sortOptions}
      />
      
      {/* Confirmation Dialog */}
      <ConfirmDialog />
    </Box>
  );
};

export default UnifiedTaskList;